import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axiosInstance from '../../utils/axiosInstance';
import DashboardLayout from '../dashboard/DashboardLayout';
import { 
  Mail, 
  Send, 
  FileText, 
  Copy, 
  Check, 
  Loader, 
  LogOut, 
  CheckCircle2, 
  AlertCircle, 
  Inbox, 
  Archive, 
  Search, 
  ChevronRight, 
  Calendar,
  X,
  Zap,
  ExternalLink,
  ShieldCheck,
  RefreshCw,
  Plus,
  Trash2
} from 'lucide-react';
import { trackPageView, trackButtonClick } from '../../utils/analytics';

const EmailPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [emailContent, setEmailContent] = useState('');
  const [subject, setSubject] = useState('');
  const [recipient, setRecipient] = useState('');
  const [copied, setCopied] = useState(false);
  const [gmailConnected, setGmailConnected] = useState(false);
  const [gmailEmail, setGmailEmail] = useState('');
  const [loadingGmail, setLoadingGmail] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendStatus, setSendStatus] = useState({ type: '', message: '' });
  const [view, setView] = useState('compose'); // 'compose' or 'history'
  const [emailLabel, setEmailLabel] = useState('INBOX'); // INBOX, SENT, DRAFT
  const [emails, setEmails] = useState([]);
  const [loadingEmails, setLoadingEmails] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [nextPageToken, setNextPageToken] = useState(null);
  const [usage, setUsage] = useState({ current: 0, limit: 100 });
  const [userTier, setUserTier] = useState('starter');

  useEffect(() => {
    trackPageView("/email", "Email Engine");
    if (location.state?.recipient) {
      setRecipient(location.state.recipient);
    }
    if (location.search.includes('connected=true')) {
      setSendStatus({ type: 'success', message: 'Gmail account connected successfully!' });
      setTimeout(() => setSendStatus({ type: '', message: '' }), 5000);
    }
    checkGmailStatus();
    fetchUsage();
  }, [location]);

  const fetchUsage = async () => {
    try {
      const response = await axiosInstance.get('/subscription/status');
      const u = response.data.usage || {};
      const l = response.data.tier?.limits || {};
      setUsage({
        current: u.hrEmails || 0,
        limit: l.hrEmails || 100
      });
      setUserTier(response.data.tier?.id || 'starter');
    } catch (err) {
      console.error('Failed to fetch usage:', err);
    }
  };

  useEffect(() => {
    if (gmailConnected && view === 'history') {
      loadEmails();
    }
  }, [gmailConnected, view, emailLabel]);

  const checkGmailStatus = async () => {
    setLoadingGmail(true);
    try {
      const response = await axiosInstance.get('/gmail/status');
      setGmailConnected(response.data.connected && response.data.is_valid);
      setGmailEmail(response.data.email || '');
    } catch (error) {
      setGmailConnected(false);
    } finally {
      setLoadingGmail(false);
    }
  };

  const handleConnectGmail = async () => {
    try {
      trackButtonClick("Connect Gmail", "Email Engine");
      const response = await axiosInstance.get('/gmail/auth');
      window.location.href = response.data.auth_url;
    } catch (error) {
      setSendStatus({ 
        type: 'error', 
        message: error.response?.data?.detail || 'Failed to connect Gmail. Please check configuration.' 
      });
    }
  };

  const handleDisconnectGmail = async () => {
    if (!window.confirm('Disconnect your Gmail account?')) return;
    try {
      trackButtonClick("Disconnect Gmail", "Email Engine");
      await axiosInstance.post('/gmail/disconnect');
      setGmailConnected(false);
      setGmailEmail('');
      setSendStatus({ type: 'success', message: 'Gmail account disconnected' });
      setTimeout(() => setSendStatus({ type: '', message: '' }), 3000);
    } catch (error) {
      setSendStatus({ type: 'error', message: 'Failed to disconnect Gmail' });
    }
  };

  const handleSendViaGmail = async () => {
    if (!recipient || !subject || !emailContent) {
      setSendStatus({ type: 'error', message: 'Please fill in all fields' });
      return;
    }
    setSending(true);
    setSendStatus({ type: '', message: '' });
    trackButtonClick("Send Email via Gmail", "Email Engine");
    try {
      await axiosInstance.post('/gmail/send', {
        to: recipient,
        subject: subject,
        body: emailContent,
        is_html: false
      });
      setSendStatus({ type: 'success', message: 'Email sent successfully!' });
      setTimeout(() => {
        setRecipient('');
        setSubject('');
        setEmailContent('');
        setSendStatus({ type: '', message: '' });
      }, 3000);
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to send email';
      setSendStatus({ type: 'error', message: errorMessage });
      if (errorMessage.includes('expired') || errorMessage.includes('reconnect')) setGmailConnected(false);
    } finally {
      setSending(false);
      fetchUsage();
    }
  };

  const handleCopy = () => {
    trackButtonClick("Copy Email to Clipboard", "Email Engine");
    const fullEmail = `Subject: ${subject}\n\nTo: ${recipient}\n\n${emailContent}`;
    navigator.clipboard.writeText(fullEmail);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const loadEmails = async (loadMore = false) => {
    if (!gmailConnected) return;
    setLoadingEmails(true);
    try {
      const params = { label: emailLabel, max_results: 20 };
      if (searchQuery) params.query = searchQuery;
      if (loadMore && nextPageToken) params.page_token = nextPageToken;
      const response = await axiosInstance.get('/gmail/emails', { params });
      if (loadMore) setEmails([...emails, ...response.data.emails]);
      else setEmails(response.data.emails || []);
      setNextPageToken(response.data.next_page_token);
    } catch (error) {
      setSendStatus({ type: 'error', message: 'Failed to load emails' });
    } finally {
      setLoadingEmails(false);
    }
  };

  const handleViewEmail = async (emailId) => {
    try {
      const response = await axiosInstance.get(`/gmail/emails/${emailId}`);
      setSelectedEmail(response.data);
    } catch (error) {
      setSendStatus({ type: 'error', message: 'Failed to load email content' });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    if (now.toDateString() === date.toDateString()) return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    if (now - date < 7 * 24 * 3600 * 1000) return date.toLocaleDateString([], { weekday: 'short' });
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  const handleTabChange = (tabId) => {
    navigate('/dashboard', { state: { tab: tabId } });
  };

  return (
    <DashboardLayout activeTab="email" setTab={handleTabChange}>
      <div className="max-w-6xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
        
        {/* Modern Tab Bar */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
           <div className="flex bg-white p-1 rounded-2xl border border-slate-200 shadow-sm w-fit">
              <button
                onClick={() => setView('compose')}
                className={`flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-black transition-all ${
                  view === 'compose' ? 'bg-blue-600 text-white shadow-lg shadow-blue-100' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-50'
                }`}
              >
                <Plus size={18} /> Compose
              </button>
              {gmailConnected && (
                <button
                  onClick={() => setView('history')}
                  className={`flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-black transition-all ${
                    view === 'history' ? 'bg-blue-600 text-white shadow-lg shadow-blue-100' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-50'
                  }`}
                >
                  <Inbox size={18} /> Inbox
                </button>
              )}
           </div>

           <div className="flex items-center gap-3">
              {gmailConnected ? (
                 <div className="flex items-center gap-3 pl-1 pr-4 py-1.5 bg-emerald-50 border border-emerald-100 rounded-2xl">
                    <div className="w-8 h-8 rounded-xl bg-emerald-500 flex items-center justify-center text-white shadow-lg shadow-emerald-100 italic font-black text-xs">GO</div>
                    <div className="flex flex-col">
                       <span className="text-[9px] font-black text-emerald-400 uppercase tracking-widest leading-none">Connected</span>
                       <span className="text-[11px] font-black text-emerald-700 mt-1 leading-none">{gmailEmail}</span>
                    </div>
                    <button onClick={handleDisconnectGmail} className="ml-2 text-slate-400 hover:text-red-500 transition-colors"><LogOut size={14} /></button>
                 </div>
              ) : (
                 <button 
                  onClick={handleConnectGmail}
                  className="flex items-center gap-2 px-6 py-2.5 bg-slate-900 text-white rounded-xl text-sm font-black hover:bg-black transition-all shadow-xl shadow-slate-200"
                 >
                    <Mail size={16} /> Link Gmail
                 </button>
              )}
           </div>
        </div>

        {sendStatus.message && (
          <div className={`flex items-center gap-3 p-4 rounded-2xl border animate-in slide-in-from-top-2 ${
            sendStatus.type === 'success' ? 'bg-emerald-50 border-emerald-100 text-emerald-700' : 'bg-red-50 border-red-100 text-red-700'
          }`}>
             {sendStatus.type === 'success' ? <CheckCircle2 size={18} /> : <AlertCircle size={18} />}
             <span className="text-sm font-bold">{sendStatus.message}</span>
          </div>
        )}

        {view === 'compose' ? (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-8 space-y-8">
               <div className="bg-white border border-slate-200 rounded-[2.5rem] p-8 shadow-sm">
                  <h3 className="text-xl font-black text-slate-900 tracking-tight flex items-center gap-2 italic mb-8">
                     <Send size={22} className="text-blue-500" /> Draft Outreach
                  </h3>

                  <div className="space-y-6">
                     <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                           <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Recipient Email</label>
                           <input
                              type="email"
                              className="w-full px-5 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-bold outline-none focus:bg-white focus:ring-4 focus:ring-blue-50 focus:border-blue-500 transition-all border-b-4 hover:border-slate-300 shadow-inner"
                              placeholder="hr@company.com"
                              value={recipient}
                              onChange={(e) => setRecipient(e.target.value)}
                           />
                        </div>
                        <div className="space-y-2">
                           <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Subject Line</label>
                           <input
                              type="text"
                              className="w-full px-5 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-bold outline-none focus:bg-white focus:ring-4 focus:ring-blue-50 focus:border-blue-500 transition-all border-b-4 hover:border-slate-300 shadow-inner"
                              placeholder="Application for Senior Role"
                              value={subject}
                              onChange={(e) => setSubject(e.target.value)}
                           />
                        </div>
                     </div>

                     <div className="space-y-2">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2 flex items-center justify-between">
                           <span>Message Body</span>
                           <span className="font-normal text-[9px] lowercase opacity-50 italic">AI generation recommended</span>
                        </label>
                        <textarea
                           className="w-full px-5 py-4 bg-slate-50 border border-slate-200 rounded-[2rem] text-sm font-medium outline-none focus:bg-white focus:ring-4 focus:ring-blue-50 focus:border-blue-500 min-h-[400px] transition-all resize-none border-b-4 shadow-inner leading-relaxed"
                           placeholder="Craft your professional message..."
                           value={emailContent}
                           onChange={(e) => setEmailContent(e.target.value)}
                        />
                     </div>

                     <div className="flex flex-wrap gap-3 pt-4 border-t border-slate-100">
                        <button
                           disabled={!gmailConnected || sending || !recipient || (usage.limit !== -1 && usage.current >= usage.limit)}
                           onClick={handleSendViaGmail}
                           className="px-10 py-3.5 bg-blue-600 text-white rounded-2xl text-sm font-black hover:bg-blue-700 disabled:opacity-50 shadow-xl shadow-blue-100 transition-all active:scale-95 flex items-center gap-2"
                        >
                           {sending ? <Loader size={18} className="animate-spin" /> : <Zap size={18} className="fill-white" />}
                           Execute Send
                        </button>
                        <button
                           onClick={handleCopy}
                           className="px-8 py-3.5 bg-white border-2 border-slate-200 text-slate-700 rounded-2xl text-sm font-black hover:hover:bg-slate-50 active:scale-95 transition-all flex items-center gap-2"
                        >
                           {copied ? <CheckCircle2 size={18} className="text-emerald-500" /> : <Copy size={18} />}
                           Copy Draft
                        </button>
                        <button
                           onClick={() => window.open(`mailto:${recipient}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(emailContent)}`)}
                           className="px-8 py-3.5 bg-white border-2 border-slate-200 text-slate-700 rounded-2xl text-sm font-black hover:hover:bg-slate-50 transition-all"
                        >
                           External Client
                        </button>
                     </div>

                     <div className="flex flex-col gap-1 max-w-sm pt-4">
                         <div className="flex items-center justify-between text-[10px] font-black uppercase tracking-widest italic text-slate-400 px-1">
                             <span>Direct Outreach Usage</span>
                             <span>{usage.limit === -1 ? 'Unlimited' : `${usage.current} / ${usage.limit}`}</span>
                         </div>
                         <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden border border-slate-200/50">
                             <div 
                                 className={`h-full transition-all duration-1000 ${usage.current >= usage.limit && usage.limit !== -1 ? 'bg-rose-500' : 'bg-blue-600'}`}
                                 style={{ width: usage.limit === -1 ? '100%' : `${Math.min((usage.current / usage.limit) * 100, 100)}%` }}
                             />
                         </div>
                         {usage.limit !== -1 && usage.current >= usage.limit && (
                             <span className="text-[9px] font-black text-rose-500 uppercase tracking-tighter mt-1 animate-pulse">Email limit reached. Upgrade to continue direct outreach.</span>
                         )}
                     </div>
                  </div>
               </div>
            </div>

            <div className="lg:col-span-4 space-y-8">
               <div className="bg-slate-900 border border-slate-800 rounded-[2.5rem] p-8 shadow-2xl relative overflow-hidden group">
                  <div className="absolute top-0 right-0 p-8 opacity-10 -rotate-12 group-hover:rotate-0 transition-transform duration-700"><ShieldCheck size={120} className="text-blue-400"/></div>
                  <h4 className="text-xs font-black text-blue-400 uppercase tracking-widest mb-6 relative z-10">Outreach Security</h4>
                  <ul className="space-y-6 relative z-10">
                    {[
                      { icon: ShieldCheck, title: "Secure Send", desc: "OAuth2 authentication ensures your credentials never leave Gmail." },
                      { icon: Zap, title: "Speed Dial", desc: "Connect Find HR contacts directly into this composer with one click." },
                      { icon: FileText, title: "Auto-Format", desc: "Professional line spacing and styling applied to every send." }
                    ].map((tip, i) => (
                      <li key={i} className="flex gap-4">
                        <div className="shrink-0 p-2 bg-slate-800 rounded-xl text-blue-400 border border-slate-700"><tip.icon size={16}/></div>
                        <div>
                           <div className="text-xs font-black text-white italic">{tip.title}</div>
                           <div className="text-[10px] text-slate-400 mt-1 leading-relaxed">{tip.desc}</div>
                        </div>
                      </li>
                    ))}
                  </ul>
               </div>

               <div className="bg-blue-600 rounded-[2.5rem] p-8 text-white relative overflow-hidden shadow-xl shadow-blue-100">
                  <h4 className="text-xs font-black uppercase tracking-widest mb-2 opacity-80 italic">Pro Tip</h4>
                  <p className="text-sm font-bold leading-relaxed">
                     Tailored outreach emails get <span className="underline decoration-2 underline-offset-4 decoration-blue-300">3x more responses</span>. Use our CV analyzer to find relevant talking points for your message.
                  </p>
               </div>
            </div>
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-[2.5rem] shadow-sm overflow-hidden flex flex-col min-h-[600px]">
             <div className="p-8 border-b border-slate-100 flex flex-col md:flex-row items-center justify-between gap-6 bg-slate-50/30">
                <div className="flex bg-white p-1 rounded-xl border border-slate-200 shadow-sm">
                   {[
                      { id: 'INBOX', icon: Inbox, label: 'Inbox' },
                      { id: 'SENT', icon: Send, label: 'Sent' },
                      { id: 'DRAFT', icon: FileText, label: 'Drafts' }
                   ].map(l => (
                      <button
                        key={l.id}
                        onClick={() => { setEmailLabel(l.id); setSelectedEmail(null); }}
                        className={`flex items-center gap-2 px-6 py-2 rounded-lg text-xs font-black transition-all ${
                          emailLabel === l.id ? 'bg-slate-900 text-white shadow-lg' : 'text-slate-500 hover:bg-slate-100'
                        }`}
                      >
                         <l.icon size={14} /> {l.label}
                      </button>
                   ))}
                </div>

                <div className="flex items-center gap-3 w-full md:w-80 relative group">
                   <input
                      type="text"
                      className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-xs font-bold outline-none focus:ring-4 focus:ring-blue-50 focus:border-blue-500 transition-all"
                      placeholder="Search mail archive..."
                      value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                      onKeyPress={e => e.key === 'Enter' && loadEmails()}
                   />
                   <Search size={14} className="absolute left-4 text-slate-400 group-focus-within:text-blue-500 transition-colors" />
                </div>
             </div>

             {selectedEmail ? (
                <div className="flex-1 flex flex-col animate-in fade-in slide-in-from-right-4 duration-300">
                   <div className="p-6 border-b border-slate-100 flex items-center justify-between">
                      <button onClick={() => setSelectedEmail(null)} className="flex items-center gap-2 text-xs font-black text-slate-500 hover:text-blue-600 transition-all uppercase tracking-widest italic">
                         <ChevronRight size={16} className="rotate-180" /> Back to list
                      </button>
                      <div className="flex gap-2">
                        <button className="p-2 bg-slate-50 text-slate-400 rounded-lg hover:text-red-500 transition-colors"><Trash2 size={16} /></button>
                      </div>
                   </div>
                   <div className="flex-1 overflow-y-auto p-10">
                      <div className="max-w-3xl mx-auto">
                        <h2 className="text-3xl font-black text-slate-900 tracking-tight mb-8 italic">{selectedEmail.subject || '(No Subject)'}</h2>
                        <div className="flex items-center justify-between mb-8 pb-8 border-b border-slate-100">
                           <div className="flex items-center gap-4">
                              <div className="w-12 h-12 bg-blue-100 rounded-2xl flex items-center justify-center text-blue-600 font-black italic">
                                 {String(selectedEmail.from_name || selectedEmail.from_email || 'U').slice(0, 1).toUpperCase()}
                              </div>
                              <div>
                                 <div className="text-sm font-black text-slate-900">{selectedEmail.from_name || 'Sender'}</div>
                                 <div className="text-[10px] font-bold text-slate-400 tracking-widest uppercase mt-0.5">{selectedEmail.from_email}</div>
                              </div>
                           </div>
                           <div className="text-right">
                              <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{formatDate(selectedEmail.date)}</div>
                              <div className="text-[10px] text-blue-500 font-bold mt-1 uppercase italic">Secure Origin</div>
                           </div>
                        </div>
                        <div className="text-sm text-slate-700 leading-relaxed font-medium font-sans whitespace-pre-wrap">
                            {selectedEmail.body_html ? (
                              <div className="email-content-reset" dangerouslySetInnerHTML={{ __html: selectedEmail.body_html }} />
                            ) : (
                                selectedEmail.body_text || selectedEmail.snippet
                            )}
                        </div>
                      </div>
                   </div>
                </div>
             ) : (
                <div className="flex-1 overflow-y-auto relative">
                   {loadingEmails && (
                      <div className="absolute inset-0 bg-white/60 backdrop-blur-sm z-20 flex flex-col items-center justify-center">
                         <Loader size={32} className="animate-spin text-blue-600 mb-4" />
                         <span className="text-xs font-black text-slate-900 uppercase tracking-widest">Hydrating Archive</span>
                      </div>
                   )}
                   
                   {emails.length === 0 ? (
                      <div className="flex flex-col items-center justify-center p-20 text-center">
                         <Inbox size={48} className="text-slate-200 mb-6" />
                         <h3 className="text-lg font-black text-slate-400 uppercase tracking-tighter italic">Folder Empty</h3>
                         <p className="text-xs text-slate-400 mt-2 max-w-xs">There are no records in your {emailLabel.toLowerCase()} at this time.</p>
                      </div>
                   ) : (
                      <div className="divide-y divide-slate-100">
                         {emails.map(e => (
                            <div
                               key={e.id}
                               onClick={() => handleViewEmail(e.id)}
                               className="p-6 hover:bg-slate-50 cursor-pointer transition-all group flex items-start gap-6 border-l-4 border-l-transparent hover:border-l-blue-600"
                            >
                               <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center text-slate-400 font-black italic text-xs group-hover:bg-blue-100 group-hover:text-blue-600 transition-colors shrink-0">
                                  {String(e.from_name || e.from_email || '?').slice(0, 1).toUpperCase()}
                               </div>
                               <div className="flex-1 min-w-0">
                                  <div className="flex items-center justify-between mb-1">
                                     <div className="text-sm font-black text-slate-900 truncate pr-4">{e.from_name || e.from_email}</div>
                                     <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest shrink-0">{formatDate(e.date)}</div>
                                  </div>
                                  <div className="text-xs font-bold text-slate-700 truncate mb-1 group-hover:text-blue-600 transition-colors uppercase italic tracking-tight">{e.subject || '(Subject Missing)'}</div>
                                  <p className="text-[11px] text-slate-400 line-clamp-1 font-medium italic">{e.snippet}</p>
                               </div>
                               <button className="opacity-0 group-hover:opacity-100 p-2 text-slate-300 hover:text-slate-600 transition-all translate-x-4 group-hover:translate-x-0"><ChevronRight size={16} /></button>
                            </div>
                         ))}
                      </div>
                   )}

                   {nextPageToken && !loadingEmails && (
                      <div className="p-8 text-center border-t border-slate-50">
                         <button onClick={() => loadEmails(true)} className="px-8 py-2 bg-slate-50 text-slate-500 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-slate-100 transition-all border border-slate-200">Load Older Records</button>
                      </div>
                   )}
                </div>
             )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default EmailPage;

// Add some basic CSS for email content resets to avoid styling spills
const styles = `
.email-content-reset a { color: #2563eb !important; text-decoration: underline !important; }
.email-content-reset img { max-width: 100% !important; height: auto !important; border-radius: 1rem; }
.email-content-reset p { margin-bottom: 1rem !important; }
`;
